/**
 * React Query hooks for Event Check-ins
 * 
 * Handles check-in functionality for events.
 */

import { useQuery, useQueryClient } from '@tanstack/react-query';
import { apiPost, type CheckinRequest } from '../lib/api';
import {
  eventsApi,
  type AdminCheckinResponse,
  type CheckinResponse,
} from '../lib/api/events';
import {
  RequestDeduplicator,
  generateIdempotencyKey
} from '../lib/deduplication';
import { useOroMutation, OroOperation } from './useOroMutation';

/**
 * Create event check-in
 * 
 * On success: Invalidates event query to refresh check-in data
 */
export function useCheckIn(eventId: string) {
  const queryClient = useQueryClient();
  
  return useOroMutation<CheckinResponse, CheckinRequest>({
    mutationFn: async (data: CheckinRequest) => {
      // ═════ Layer 1: Frontend request deduplication ═════
      const deduplicator = RequestDeduplicator.getInstance();
      const idempotencyKey = generateIdempotencyKey(); // No userId – server-side scoped
      
      // Check if duplicate request already pending
      const pending = deduplicator.checkDuplicate(
        `POST /events/${eventId}/checkins`,
        data
      );
      
      if (pending) {
        console.log('[useCheckIn] Returning pending request for duplicate');
        return pending.promise as Promise<CheckinResponse>;
      }
      
      // Create request with abort controller
      const controller = new AbortController();
      
      const promise = apiPost<CheckinResponse>(
        `/events/${eventId}/checkins`,
        data,
        {
          idempotencyKey,
          signal: controller.signal,
        }
      ).catch((e: Error) => {
        if (e.name === 'AbortError') {
          console.log('[useCheckIn] Request aborted (duplicate detected)');
        }
        throw e;
      });
      
      // Register with deduplicator
      deduplicator.register(
        `POST /events/${eventId}/checkins`,
        data,
        promise,
        controller
      );
      
      return promise;
      // ═════ End Layer 1 ═════
    },
    onSuccess: () => {
      // Refresh event data to show updated check-in status
      queryClient.invalidateQueries({ 
        queryKey: ['event', eventId] 
      });
    },
    successToast: 'Check-in successful',
    oro: {
      op: OroOperation.CheckIn,
      retryPolicy: 'never',
      requiresIdempotency: true,
      debugLabel: 'Event check-in',
    },
  });
}

/**
 * List who has checked in to an event (admin/leader only).
 * Backend returns a bare array. Leader/owner gate is enforced server-side.
 * Auth-protected → gate on userId (Core Rule 19), like useEventRSVPs.
 */
export function useEventCheckins(eventId: string, userId?: string | null) {
  return useQuery({
    queryKey: ['event-checkins', eventId],
    queryFn: () => eventsApi.getCheckins(eventId),
    enabled: !!eventId && !!userId,
    // Surface fetch errors immediately (no 3x retry backoff) so the admin
    // check-in roster shows its error state instead of a transient false-empty
    // while retries run. Matches useEventRSVPs. Single consumer (#2183).
    retry: false,
  });
}

/**
 * Admin/leader manual check-in of a club member (#1978).
 *
 * On success: refreshes the check-ins, RSVPs, and event queries so the
 * member moves into the "checked in" group. Server enforces leader/owner +
 * club-membership + time-window; surfaces canonical error toasts on failure.
 */
export function useAdminCheckin(eventId: string) {
  const queryClient = useQueryClient();

  return useOroMutation<AdminCheckinResponse, { user_id: string }>({
    mutationFn: ({ user_id }: { user_id: string }) =>
      eventsApi.adminCheckin(eventId, user_id, generateIdempotencyKey()),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['event-checkins', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event-rsvps', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
    },
    successToast: 'Member checked in',
    oro: {
      op: OroOperation.CheckIn,
      retryPolicy: 'never',
      requiresIdempotency: true,
      debugLabel: 'Admin check-in',
    },
  });
}
